import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { Button, Avatar, useMe, Form, Input } from 'src/common';
import { notUndefined } from 'src/helpers/typescript';
import { IApiComment, CommentParentType } from 'src/models/comments/types';
import UserName from 'src/common/UserName';
import { trackAddComment } from 'src/segment/events';
import { IUserName, DELETED_USER } from 'src/models/users/types';
import { useCreateComment } from 'src/models/comments/hooks';
import classNames from 'classnames';
import { CommentsMode } from '..';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-mention';
import ReactDOMServer from 'react-dom/server';
import { KeyCodes, NodeNames } from 'src/helpers/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes } from '@fortawesome/pro-light-svg-icons';
import { useFeatureFlag, FeatureFlag } from 'src/helpers/featureFlag';

const mentionClass = 'mention';
const mentionFocusedClass = 'focused';

function getMentionedNames(contents: readonly string[]): Set<string> {
  return contents.reduce((previousValue, currentValue) => {
    const el = document.createElement('html');
    el.innerHTML = currentValue;
    const records = el.getElementsByTagName('span');
    for (const record of records) {
      const res = record.getAttribute('data-id');
      res && previousValue.add(res);
    }
    return previousValue;
  }, new Set<string>());
}

interface IMentionUserProps {
  user: IUserName;
}
interface IMentionProps extends IUserName {
  value: string;
  id: string;
}
const MentionUser: FC<IMentionUserProps> = props => {
  const { user } = props;
  return (
    <div className={styles.mentionDropdownItemInner}>
      <UserName user={user} size={'s'} className={styles.mentionDropdownUser} />
    </div>
  );
};

interface IProps {
  onCancel?: () => void;
  parentId: string;
  existingComments?: readonly IApiComment[];
  parentType: CommentParentType;
  mode: CommentsMode;
  users: IUserName[];
}

interface InsertMentionElement {
  denotationChar: string;
  id: string;
  index: string;
  mentionIndex: string;
  value: string;
}

interface InsertMention {
  mention: DOMStringMap & { mention: InsertMentionElement };
}

interface QuillEditorElement {
  insert: string | InsertMention;
}

interface QuillEditorContents {
  ops: QuillEditorElement[];
}

interface CaretPositionParams {
  position: number;
  leftOffset?: number;
  isRemoving?: boolean;
}

interface IFormState {
  shareWith: string;
}

export const CommentCreate: FC<IProps> = (props: IProps) => {
  const { onCancel, existingComments, parentId, parentType, mode, users } = props;
  const [isTouched, setIsTouched] = useState(false);
  const { t } = useTranslation();

  const [isSharing, setIsSharing] = useState(false);
  const onToggleShare = useCallback(() => {
    setIsSharing(!isSharing);
  }, [isSharing]);
  const onHideShare = useCallback(() => {
    setIsSharing(false);
  }, []);

  const { data: me } = useMe();

  const isGuestFeature = useFeatureFlag(FeatureFlag.Interactions_ShareWithGuestUsers);

  const [content, setContent] = useState('');
  const [parsedContent, setParsedContent] = useState('');

  const ref = useRef<HTMLDivElement>(null);
  const sendButtonRef = React.createRef<HTMLButtonElement>();
  const [mentions, setMentions] = useState(document.getElementsByClassName('mention'));
  const [mentionPositionsMap, setMentionPositionsMap] = useState(new Map<number, string>());

  const [form] = Form.useForm();
  const initialValues = useMemo((): IFormState => {
    return {
      shareWith: ''
    };
  }, []);

  let mentionIndex = 0;
  const mentionRef = useRef<ReactQuill>(null);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const quillEditorRef = useRef<Quill.Quill>(null);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const quillUnprivilegedEditorRef = useRef<ReactQuill.UnprivilegedEditor>(null);

  const companyUsers = useMemo(() => {
    const mentionables = new Map<string, IUserName>();
    users.forEach(user => {
      const { lastName, name, email } = user;
      const displayName = name && lastName ? `${name} ${lastName}` : `${email}`;
      mentionables.set(displayName, user);
    });
    return mentionables;
  }, [users]);

  const [createComment, { loading: isLoading }] = useCreateComment();

  const onClose = useCallback(() => {
    setContent('');
    onHideShare();
    form.setFieldsValue({ shareWith: '' });
    setIsTouched(false);
  }, [form, onHideShare]);

  const onCreate = useCallback(
    (mentions: string[]) => {
      trackAddComment(parentId, mentions);
      onClose();
    },
    [onClose, parentId]
  );

  const onFinish = useCallback(
    (values: IFormState) => {
      if (!!parsedContent) {
        const { shareWith } = values;
        const names = getMentionedNames([content]);
        const previousNames = getMentionedNames(existingComments?.map(c => c.content) || []);
        const mentions = Array.from(names).filter(notUndefined);
        const previousMentions = Array.from(previousNames).filter(notUndefined);
        createComment({
          content,
          parentId,
          mentions,
          previousMentions,
          parentType,
          onCreate,
          shareWith: isGuestFeature ? shareWith : undefined
        });
      }
    },
    [content, createComment, existingComments, isGuestFeature, onCreate, parentId, parentType, parsedContent]
  );

  const onClickCancel = useCallback(() => {
    onCancel && onCancel();
    onClose();
    !!mentionRef.current && mentionRef.current.blur();
  }, [onCancel, onClose]);

  const onCommentFieldClick = useCallback(() => {
    setIsTouched(true);
  }, []);

  const usersList = useMemo(() => {
    return Array.from(companyUsers.entries())
      .map(([, user]) => ({
        id: user.email,
        value: user.userName,
        ...user
      }))
      .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : -1));
  }, [companyUsers]);

  const modules = useMemo(() => {
    return {
      toolbar: false,
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: function () {
              /* empty fn */
            }
          }
        }
      },
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö0-9_+.-]*$/,
        mentionDenotationChars: ['@'],
        listItemClass: styles.mentionDropdownItem,
        mentionListClass: styles.mentionDropdown,
        mentionContainerClass: styles.mentionDropdownContainer,
        isolateCharacter: true,
        renderItem: (item: IMentionProps) => ReactDOMServer.renderToString(<MentionUser user={item} />),
        source: (
          searchTerm: string,
          renderList: (data: { id: string; value: string }[], searchTerm?: string) => void
        ) => {
          let matches;
          if (!!searchTerm.length) {
            const firstMatches = usersList
              .filter(el => el.value.toLowerCase().indexOf(searchTerm.toLowerCase()) === 0)
              .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : -1));
            const otherMatches = usersList
              .filter(el => el.value.toLowerCase().indexOf(searchTerm.toLowerCase()) > 0)
              .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : -1));
            matches = firstMatches.concat(otherMatches);
          } else {
            matches = usersList;
          }
          renderList(matches, searchTerm);
        }
      }
    };
  }, [usersList]);

  const onMentionFocus = useCallback((event: Event) => {
    (event.target as HTMLElement).classList.add('focused');
  }, []);

  const onMentionBlur = useCallback((event: Event) => {
    (event.target as HTMLElement).classList.remove('focused');
  }, []);

  useEffect(() => {
    const currMention = ref.current?.getElementsByClassName('mention');
    currMention && setMentions(currMention);
    if (mentionRef.current?.getEditor()) {
      quillEditorRef.current = mentionRef.current?.getEditor();
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    quillUnprivilegedEditorRef.current = mentionRef.current?.makeUnprivilegedEditor(quillEditorRef.current);
    return () => {
      for (const mention of mentions) {
        if (
          !Object.prototype.hasOwnProperty.call(mention.attributes, 'contenteditable') &&
          Object.prototype.hasOwnProperty.call(mention.attributes, 'tabindex')
        ) {
          mention.removeAttribute('tabindex');
          mention.removeEventListener('focus', onMentionFocus);
          mention.removeEventListener('blur', onMentionBlur);
        }
      }
    };
  }, [mentions, onMentionFocus, onMentionBlur]);

  const collectEditableMentions = useCallback((): void => {
    for (const mention of mentions) {
      // adding to mentions in editor possibility to be focused (via tabindex)
      if (
        !Object.prototype.hasOwnProperty.call(mention.attributes, 'contenteditable') &&
        !Object.prototype.hasOwnProperty.call(mention.attributes, 'tabindex')
      ) {
        mention.setAttribute('tabindex', '0');
        mention.addEventListener('focus', onMentionFocus);
        mention.addEventListener('blur', onMentionBlur);
      }
      // adding to mentions indexes to easy manage them on events detecting
      if (!Object.prototype.hasOwnProperty.call(mention.attributes, 'data-mention-index')) {
        mention.setAttribute('data-mention-index', mentionIndex.toString());
        mentionIndex++;
      }
    }
    // adding to mentions their position as a node inside editor
    const updatedMentionPositionsMap = new Map<number, string>();
    let contentString = '';
    const content: QuillEditorContents = quillUnprivilegedEditorRef.current?.getContents();
    content?.ops?.forEach((opsEl: QuillEditorElement, index) => {
      if (typeof opsEl.insert === 'string') {
        // forming only text content of the editor to count it length
        contentString += opsEl.insert;
      } else if (typeof opsEl.insert === 'object') {
        for (const mention of mentions) {
          const mentionElIndex = mention.getAttribute('data-mention-index');
          if (opsEl.insert.mention.mentionIndex === mentionElIndex) {
            const mentionPosition = contentString.length + 1;
            mention.setAttribute('data-element-position', `${index}`);
            updatedMentionPositionsMap.set(mentionPosition, mentionElIndex);
          }
        }
        contentString += ' ';
      }
    });
    setMentionPositionsMap(updatedMentionPositionsMap);
  }, [mentions, onMentionFocus, onMentionBlur, mentionIndex]);

  const onChange = useCallback(
    content => {
      setContent(content);
      collectEditableMentions();
      setParsedContent(content.replace(/<p>|<br>|<\/p>/g, '').trim());
    },
    [collectEditableMentions]
  );

  const checkIsMentionFocused = (event: KeyboardEvent): boolean => {
    const target = event.target as HTMLElement;
    const isMentionTarget = target.nodeName === NodeNames.Span && target.classList.contains(mentionClass);
    const isMentionFocused = target.classList.contains(mentionFocusedClass);
    return isMentionTarget && isMentionFocused;
  };

  const setCaretPosition = useCallback((params: CaretPositionParams): void => {
    const { position, leftOffset = 0, isRemoving = false } = params;
    const range = document.createRange();
    const sel = window.getSelection();

    let rootElement = quillEditorRef.current.root.childNodes[0];
    let selectionPosition = position;

    if (isRemoving && !!position && !!leftOffset) {
      rootElement = quillEditorRef.current.root.childNodes[0].childNodes[position - 1];
      selectionPosition = leftOffset;
    }

    range.setStart(rootElement, selectionPosition);
    range.collapse(true);

    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  const removeMention = useCallback(
    (event: KeyboardEvent, mentionPosition: number, callback?: (s: string) => void): void => {
      let selectionPosition = 0;
      if (!!mentionPosition) {
        const leftSibling = quillEditorRef.current.root.childNodes[0].childNodes[mentionPosition - 1];
        if (leftSibling.length !== undefined) {
          selectionPosition = leftSibling.length;
        }
      }
      (event.target as HTMLElement).remove();
      setTimeout(() => {
        setCaretPosition({ position: mentionPosition, leftOffset: selectionPosition, isRemoving: true });
        if (callback) {
          callback(String.fromCharCode(event.keyCode).toLowerCase());
        }
      }, 100);
    },
    [setCaretPosition]
  );

  const onKeyUp = useCallback(
    event => {
      if (event.keyCode === KeyCodes.Tab) {
        sendButtonRef.current?.focus();
        return;
      }
      if (checkIsMentionFocused(event)) {
        // position of mention node inside P node of editor
        const mentionPosition = +event.target.getAttribute('data-element-position');
        switch (event.keyCode) {
          case KeyCodes.Backspace:
            removeMention(event, mentionPosition);
            break;
          case KeyCodes.ArrowLeft:
            setCaretPosition({ position: mentionPosition });
            break;
          case KeyCodes.ArrowRight:
            setCaretPosition({ position: mentionPosition + 1 });
            break;
        }
      } else {
        const currentSelection = quillUnprivilegedEditorRef.current?.getSelection();
        if (mentionPositionsMap.has(currentSelection?.index)) {
          const mentionIndex = mentionPositionsMap.get(currentSelection?.index);
          for (const mention of mentions) {
            const mentionElIndex = mention.getAttribute('data-mention-index');
            if (mentionIndex === mentionElIndex) {
              const sel = window.getSelection();
              sel?.removeAllRanges();
              (mention as HTMLElement).focus();
            }
          }
        }
      }
    },
    [sendButtonRef, removeMention, setCaretPosition, mentionPositionsMap, mentions]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const insertText = (text: string): void => {
        const range = quillEditorRef.current.getSelection();
        const position = range ? range.index : 0;
        quillEditorRef.current.insertText(position, text);
      };

      if (checkIsMentionFocused(event)) {
        // position of mention node inside P node of editor
        const mentionPosition = (event.target as HTMLElement).getAttribute('data-element-position');
        if (
          (event.keyCode >= KeyCodes.KeyA && event.keyCode <= KeyCodes.KeyZ) ||
          (event.keyCode >= KeyCodes.Digit0 && event.keyCode <= KeyCodes.Digit9)
        ) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          removeMention(event, +mentionPosition!, insertText);
        }
      }
    },
    [removeMention]
  );

  return (
    <div className={classNames(styles.commentForm, { [styles[mode]]: mode })} ref={ref}>
      <Form
        form={form}
        onFinish={onFinish}
        layout={'vertical'}
        initialValues={initialValues}
        className={styles.commentFormInner}
      >
        <Avatar user={me || DELETED_USER} className={styles.commentPic} />
        <div className={styles.commentFieldset}>
          {isGuestFeature && (
            <Form.Item
              className={classNames(styles.sharingField, { [styles.isSharing]: isSharing })}
              name={'shareWith'}
              rules={[
                {
                  type: 'email',
                  message: t('Common.incorrectEmailError'),
                  validateTrigger: 'onBlur'
                }
              ]}
            >
              <Input placeholder={t('Comments.shareToEmail')} autoComplete={'email'} type={'email'} />
            </Form.Item>
          )}
          <div className={styles.commentField} onClick={onCommentFieldClick}>
            <ReactQuill
              className={classNames(styles.commentInput, { [styles.isFocused]: isTouched })}
              theme="snow"
              value={content}
              onChange={onChange}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
              modules={modules}
              placeholder={t('Comments.addNewComment')}
              ref={mentionRef}
            />
          </div>
          {isTouched && (
            <div className={styles.commentSubmit}>
              <Button
                className={styles.submitButton}
                htmlType={'submit'}
                data-action={'addComment'}
                loading={isLoading}
                type={'primary'}
                ref={sendButtonRef}
                tabIndex={0}
                disabled={!parsedContent}
              >
                {t(isSharing ? 'Comments.shareComment' : 'Comments.addComment')}
              </Button>
              <Button onClick={onClickCancel} type={'text'}>
                {t('Common.cancel')}
              </Button>
              {isGuestFeature && (
                <Button
                  className={styles.shareButton}
                  onClick={onToggleShare}
                  data-action={'share'}
                  tabIndex={0}
                  icon={<FontAwesomeIcon icon={faShareNodes} />}
                >
                  {t('Comments.share')}
                </Button>
              )}
            </div>
          )}
        </div>
      </Form>
    </div>
  );
};

export default CommentCreate;
